import { useState, useEffect, useRef } from "react";
import axios from "axios";

// ─── Replace with your actual base API URL ────────────────────────────────────
const baseAPI = process.env.REACT_APP_API_URL;


// ─── API helpers ──────────────────────────────────────────────────────────────
const fetchLeadById = async (leadId) => {
  const storedToken = JSON.parse(localStorage.getItem("token"));
  if (!storedToken) throw new Error("No token found");

  const res = await axios.get(`${baseAPI}userget/GetPetDetailForRate?relocation_id=${leadId}`, {
    headers: { Authorization: `Bearer ${storedToken}` }
  });

  if (res.data.status === "True" && res.data.data && res.data.data.length > 0) {
    const data = res.data.data[0];
    return {
      // airline_name from API may be comma-separated; split into array
      airlinesName: data.airline_name
        ? data.airline_name.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
      originAirport: data.origin_airport || "",
      destinationAirport: data.destination_airport || "",
      petType: data.Pet_Type ? data.Pet_Type.toString() : "",
      petBreed: data.Pet_Breed ? data.Pet_Breed.toString() : "",
      dob: data.Pet_DOB ? data.Pet_DOB.split('T')[0] : "",
      petDimensions: data.pet_dimension || "",
      volumetricWeight: data.chargeable_wt || "",
      petCrate: data.petCrate || "",
    };
  }
  return null;
};

const submitRateRequest = async (payload, leadId) => {
  const storedToken = JSON.parse(localStorage.getItem("token"));
  if (!storedToken) throw new Error("No token found");

  const requestBody = {
    lead_id: parseInt(leadId),
    // Join selected airlines as comma-separated string
    airlines_name: Array.isArray(payload.airlinesName)
      ? payload.airlinesName.join(", ")
      : payload.airlinesName,
    origin_airport: payload.originAirport,
    destination_airport: payload.destinationAirport,
    gross_weight: parseFloat(payload.grossWeight),
    pet_type_id: parseInt(payload.petType),
    pet_breed_id: parseInt(payload.petBreed),
    dob: payload.dob,
    pet_dimensions: payload.petDimensions,
    volumetric_weight: parseFloat(payload.volumetricWeight),
    freight_charges: payload.freightCharges,
    overseas_charges: payload.overseasCharges,
    petCrate: payload.petCrate,
    custom_crate_size: payload.customCrateSize || "", 
    overseas_requested_for: Array.isArray(payload.overseasRequestedFor)
      ? payload.overseasRequestedFor.join(", ")
      : payload.overseasRequestedFor || "",
  };

  const res = await axios.post(`${baseAPI}userpost/SubmitRateRequest`, requestBody, {
    headers: { Authorization: `Bearer ${storedToken}` }
  });

  return res.data;
};

const getPetTypes = async () => {
  const storedToken = JSON.parse(localStorage.getItem("token"));
  if (!storedToken) throw new Error("No token found");

  const res = await axios.get(`${baseAPI}adminget/GEPetType?PetType=0`, {
    headers: { Authorization: `Bearer ${storedToken}` },
  });
  return res.data.data;
};

const getAirports = async () => {
  const storedToken = JSON.parse(localStorage.getItem("token"));
  if (!storedToken) throw new Error("No token found");

  const res = await axios.get(`${baseAPI}adminget/GetAirport`, {
    headers: { Authorization: `Bearer ${storedToken}` },
  });
  return res.data.data;
};


const getPetBreeds = async (petTypeId) => {
  const storedToken = JSON.parse(localStorage.getItem("token"));
  if (!storedToken) throw new Error("No token found");

  const petType = petTypeId || 0;
  const res = await axios.get(
    `${baseAPI}adminget/GEPetBreedType?PetBreedID=0&PetType=${petType}`,
    { headers: { Authorization: `Bearer ${storedToken}` } }
  );
  return res.data.data;
};

// ─── NEW: Fetch airline names ─────────────────────────────────────────────────
const getAirlinesName = async () => {
  const storedToken = JSON.parse(localStorage.getItem("token"));
  if (!storedToken) throw new Error("No token found");

  const res = await axios.get(`${baseAPI}userget/GetAirlinesNames`, {
    headers: { Authorization: `Bearer ${storedToken}` },
  });
  return res.data.data;
};

// ─── Initial form state ───────────────────────────────────────────────────────
const BLANK_FORM = {
  airlinesName: [],        // now an array of selected airline names/ids
  originAirport: "",
  destinationAirport: "",
  grossWeight: "",
  petType: "",
  petBreed: "",
  dob: "",
  petDimensions: "",
  volumetricWeight: "",
  freightCharges: false,
  overseasCharges: false,
  petCrate: "",
  overseasRequestedFor: [],
  customCrateSize: ""
};

const OVERSEAS_OPTIONS = [
  "Import Permit",
  "Import Custom Clearance",
  "Door-to-Door",
  "Quarantine Charges",
  "CDC Dog Import Clearance",
  "Export Rates",
];

// ─── Styles ───────────────────────────────────────────────────────────────────
const css = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(10, 8, 30, 0.60)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 99999,
    padding: "16px",
    boxSizing: "border-box",
  },
  modal: {
    backgroundColor: "#ffffff",
    borderRadius: "16px",
    width: "100%",
    maxWidth: "760px",
    maxHeight: "90vh",
    overflowY: "auto",
    overflowX: "hidden",
    boxShadow: "0 20px 60px rgba(10,8,30,0.28)",
    display: "flex",
    flexDirection: "column",
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontSize: "14px",
    color: "#1a1a2e",
    boxSizing: "border-box",
  },
  header: {
    background: "linear-gradient(135deg, #F15843, #A3519F, #5B489D)",
    color: "#fff",
    borderRadius: "16px 16px 0 0",
    padding: "5px 5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: "12px" },
  headerIcon: {
    width: "36px",
    height: "36px",
    borderRadius: "9px",
    backgroundColor: "rgba(255,255,255,0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "17px",
    flexShrink: 0,
  },
  headerTitle: {
    color: "#fff",
    margin: 0,
    fontSize: "15px",
    fontWeight: 700,
    lineHeight: 1.3,
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  headerSub: {
    color: "rgba(255,255,255,0.70)",
    fontSize: "12px",
    margin: "2px 0 0",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  closeBtn: {
    background: "rgba(255,255,255,0.15)",
    border: "none",
    color: "#fff",
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    lineHeight: 1,
    padding: 0,
  },
  loadingWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    gap: "14px",
  },
  loadingText: {
    fontSize: "13px",
    color: "#888",
    fontFamily: "system-ui, -apple-system, sans-serif",
    margin: 0,
  },
  skelGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "10px",
    width: "100%",
  },
  skel: {
    height: "36px",
    borderRadius: "7px",
    backgroundColor: "#ede9fe",
    opacity: 0.6,
  },
  body: { padding: "22px 24px 16px", flexGrow: 1 },
  sectionRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
    marginTop: "4px",
  },
  sectionText: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.13em",
    color: "#7c3aed",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  sectionLine: {
    flex: 1,
    height: "1px",
    background: "linear-gradient(to right, #ddd6fe, transparent)",
  },
  grid1: { marginBottom: "12px" },
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    marginBottom: "12px",
  },
  grid3: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "12px",
    marginBottom: "12px",
  },
  fieldWrap: { display: "flex", flexDirection: "column", gap: "4px" },
  label: {
    fontSize: "11px",
    fontWeight: 600,
    color: "#555",
    letterSpacing: "0.03em",
    margin: 0,
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  autoChip: {
    display: "inline-block",
    fontSize: "9px",
    background: "#dcfce7",
    color: "#15803d",
    borderRadius: "3px",
    padding: "1px 5px",
    marginLeft: "5px",
    fontWeight: 700,
    letterSpacing: "0.05em",
    verticalAlign: "middle",
  },
  input: {
    padding: "8px 11px",
    border: "1.5px solid #e0ddf5",
    borderRadius: "7px",
    fontSize: "13px",
    fontFamily: "system-ui, -apple-system, sans-serif",
    outline: "none",
    backgroundColor: "#fff",
    color: "#1a1a2e",
    width: "100%",
    boxSizing: "border-box",
    lineHeight: "1.4",
  },
  inputAuto: {
    padding: "8px 11px",
    border: "1.5px solid #bbf7d0",
    borderRadius: "7px",
    fontSize: "13px",
    fontFamily: "system-ui, -apple-system, sans-serif",
    outline: "none",
    backgroundColor: "#f0fdf4",
    color: "#166534",
    fontWeight: 500,
    width: "100%",
    boxSizing: "border-box",
    lineHeight: "1.4",
    cursor: "default",
  },
  inputWithUnit: { position: "relative" },
  unitLabel: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize: "11px",
    color: "#aaa",
    fontWeight: 600,
    pointerEvents: "none",
  },
  select: {
    padding: "8px 28px 8px 11px",
    border: "1.5px solid #e0ddf5",
    borderRadius: "7px",
    fontSize: "13px",
    fontFamily: "system-ui, -apple-system, sans-serif",
    outline: "none",
    backgroundColor: "#fff",
    color: "#1a1a2e",
    width: "100%",
    boxSizing: "border-box",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    lineHeight: "1.4",
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%237c3aed' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
  },
  selectAuto: {
    padding: "8px 28px 8px 11px",
    border: "1.5px solid #bbf7d0",
    borderRadius: "7px",
    fontSize: "13px",
    fontFamily: "system-ui, -apple-system, sans-serif",
    outline: "none",
    backgroundColor: "#f0fdf4",
    color: "#166534",
    fontWeight: 500,
    width: "100%",
    boxSizing: "border-box",
    cursor: "not-allowed",
    appearance: "none",
    WebkitAppearance: "none",
    lineHeight: "1.4",
  },
  errMsg: {
    fontSize: "11px",
    color: "#b91c1c",
    margin: "2px 0 0",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  chargesRow: {
    display: "flex",
    gap: "10px",
    marginBottom: "16px",
    flexWrap: "wrap",
  },
  checkCard: {
    display: "flex",
    alignItems: "center",
    gap: "9px",
    padding: "10px 16px",
    border: "1.5px solid #e0ddf5",
    borderRadius: "9px",
    cursor: "pointer",
    userSelect: "none",
    flex: 1,
    minWidth: "140px",
    backgroundColor: "#fff",
    boxSizing: "border-box",
  },
  checkCardOn: { borderColor: "#6d28d9", backgroundColor: "#f5f3ff" },
  checkbox: {
    width: "16px",
    height: "16px",
    borderRadius: "4px",
    border: "2px solid #ccc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxSizing: "border-box",
  },
  checkboxOn: { backgroundColor: "#6d28d9", borderColor: "#6d28d9" },
  footer: {
    borderTop: "1px solid #f0eeff",
    padding: "14px 24px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "10px",
    flexShrink: 0,
    backgroundColor: "#fff",
    borderRadius: "0 0 16px 16px",
  },
  footNote: {
    fontSize: "11px",
    color: "#bbb",
    marginRight: "auto",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },
  cancelBtn: {
    padding: "8px 18px",
    backgroundColor: "transparent",
    border: "1.5px solid #e0ddf5",
    borderRadius: "7px",
    cursor: "pointer",
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontWeight: 600,
    fontSize: "13px",
    color: "#666",
    lineHeight: "1",
  },
  submitBtn: {
    padding: "8px 22px",
    background: "linear-gradient(135deg, #4338ca, #7c3aed)",
    color: "#fff",
    border: "none",
    borderRadius: "7px",
    cursor: "pointer",
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontWeight: 700,
    fontSize: "13px",
    letterSpacing: "0.04em",
    boxShadow: "0 3px 12px rgba(109,40,217,0.30)",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minWidth: "130px",
    justifyContent: "center",
    lineHeight: "1",
  },
  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    padding: "12px 18px",
    borderRadius: "9px",
    fontFamily: "system-ui, -apple-system, sans-serif",
    fontSize: "13px",
    fontWeight: 600,
    zIndex: 100000,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    boxShadow: "0 6px 24px rgba(0,0,0,0.16)",
    color: "#fff",
    maxWidth: "320px",
  },
};

// ─── Spinner ──────────────────────────────────────────────────────────────────
function Spinner({ size = 16, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" stroke={color} strokeOpacity="0.25" strokeWidth="3" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth="3" strokeLinecap="round">
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 12 12"
          to="360 12 12"
          dur="0.7s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  );
}

// ─── Field ────────────────────────────────────────────────────────────────────
function Field({ label, error, children }) {
  return (
    <div style={css.fieldWrap}>
      <p style={css.label}>{label}</p>
      {children}
      {error && <p style={css.errMsg}>⚠ {error}</p>}
    </div>
  );
}

// ─── Section ──────────────────────────────────────────────────────────────────
function Section({ title }) {
  return (
    <div style={css.sectionRow}>
      <span style={css.sectionText}>{title}</span>
      <div style={css.sectionLine} />
    </div>
  );
}

// ─── HoverInput ───────────────────────────────────────────────────────────────
function HoverInput({ autoFilled, style, ...props }) {
  const [focused, setFocused] = useState(false);
  const base = autoFilled ? css.inputAuto : css.input;
  const focusExtra =
    focused && !autoFilled
      ? { borderColor: "#6d28d9", boxShadow: "0 0 0 3px rgba(109,40,217,0.10)" }
      : {};
  return (
    <input
      style={{ ...base, ...focusExtra, ...style }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      {...props}
    />
  );
}

// ─── HoverSelect ──────────────────────────────────────────────────────────────
function HoverSelect({ autoFilled, style, children, ...props }) {
  const [focused, setFocused] = useState(false);
  const base = autoFilled ? css.selectAuto : css.select;
  const focusExtra =
    focused && !autoFilled
      ? { borderColor: "#6d28d9", boxShadow: "0 0 0 3px rgba(109,40,217,0.10)" }
      : {};
  return (
    <select
      style={{ ...base, ...focusExtra, ...style }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      {...props}
    >
      {children}
    </select>
  );
}

// ─── AirlineMultiSelect ───────────────────────────────────────────────────────
// Dropdown with checkboxes; selected values shown as comma-separated chips
function AirlineMultiSelect({ options = [], selected = [], onChange, autoFilled, error }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter((opt) => {
    const label = opt.airline_name || opt.name || opt.label || String(opt);
    return label.toLowerCase().includes(search.toLowerCase());
  });

  const toggleOption = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const displayText = selected.length === 0
    ? "Select airlines"
    : selected.join(", ");

  const triggerBorderColor = error
    ? "#b91c1c"
    : autoFilled
      ? "#bbf7d0"
      : open
        ? "#6d28d9"
        : "#e0ddf5";

  const triggerBg = autoFilled ? "#f0fdf4" : "#fff";
  const triggerColor = autoFilled ? "#166534" : selected.length > 0 ? "#1a1a2e" : "#999";

  const PET_CRATE_LABELS = {
    "17": "(24\" X 16\" X 16\")",
    "35": "(28\" X 21\" X 22\")",
    "48": "(32\" X 23\" X 24\")",
    "66": "(36\" X 25\" X 27\")",
    "89": "(40\" X 27\" X 30\")",
    "147": "(48\" X 32\" X 35\")",
    "CR82": "CR82",
    "Soft Bag": "Soft Bag",
  };


  // ─── AirportSelect ────────────────────────────────────────────────────────────




  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      {/* Trigger button */}
      <div
        onClick={() => { setOpen((o) => !o); setSearch(""); }}
        style={{
          padding: "8px 32px 8px 11px",
          border: `1.5px solid ${triggerBorderColor}`,
          borderRadius: open ? "7px 7px 0 0" : "7px",
          fontSize: "13px",
          fontFamily: "system-ui, -apple-system, sans-serif",
          backgroundColor: triggerBg,
          color: triggerColor,
          cursor: "pointer",
          userSelect: "none",
          width: "100%",
          boxSizing: "border-box",
          lineHeight: "1.4",
          position: "relative",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontWeight: autoFilled ? 500 : 400,
          boxShadow: open ? "0 0 0 3px rgba(109,40,217,0.10)" : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      >
        {displayText}
        {/* Chevron */}
        <span style={{
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: `translateY(-50%) rotate(${open ? "180deg" : "0deg"})`,
          transition: "transform 0.2s",
          lineHeight: 1,
          pointerEvents: "none",
        }}>
          <svg width="10" height="6" viewBox="0 0 10 6">
            <path d="M1 1l4 4 4-4" stroke="#7c3aed" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          </svg>
        </span>
      </div>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          backgroundColor: "#fff",
          border: "1.5px solid #6d28d9",
          borderTop: "none",
          borderRadius: "0 0 9px 9px",
          zIndex: 9999,
          boxShadow: "0 8px 24px rgba(109,40,217,0.14)",
          maxHeight: "220px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
          {/* Search input */}
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f0eeff" }}>
            <input
              autoFocus
              placeholder="Search airlines…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                padding: "6px 10px",
                border: "1.5px solid #e0ddf5",
                borderRadius: "6px",
                fontSize: "12px",
                fontFamily: "system-ui, -apple-system, sans-serif",
                outline: "none",
                boxSizing: "border-box",
                color: "#1a1a2e",
              }}
            />
          </div>

          {/* Options list */}
          <div style={{ overflowY: "auto", flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{
                padding: "12px 14px",
                fontSize: "12px",
                color: "#aaa",
                fontFamily: "system-ui, -apple-system, sans-serif",
                textAlign: "center",
              }}>
                No airlines found
              </div>
            ) : (
              filtered.map((opt) => {
                const label = opt.airline_name || opt.name || opt.label || String(opt);
                const value = opt.airline_name || opt.name || opt.label || String(opt);
                const checked = selected.includes(value);

                return (
                  <div
                    key={value}
                    onClick={(e) => { e.stopPropagation(); toggleOption(value); }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "8px 14px",
                      cursor: "pointer",
                      backgroundColor: checked ? "#f5f3ff" : "transparent",
                      transition: "background-color 0.1s",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!checked) e.currentTarget.style.backgroundColor = "#faf5ff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = checked ? "#f5f3ff" : "transparent";
                    }}
                  >
                    {/* Checkbox */}
                    <div style={{
                      width: "15px",
                      height: "15px",
                      borderRadius: "4px",
                      border: checked ? "2px solid #6d28d9" : "2px solid #ccc",
                      backgroundColor: checked ? "#6d28d9" : "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.15s",
                    }}>
                      {checked && (
                        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                          <path d="M1 4L3 6.5L7 1.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <span style={{
                      fontSize: "13px",
                      fontFamily: "system-ui, -apple-system, sans-serif",
                      color: checked ? "#4c1d95" : "#333",
                      fontWeight: checked ? 600 : 400,
                    }}>
                      {label}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer: count + clear */}
          {selected.length > 0 && (
            <div style={{
              padding: "6px 12px",
              borderTop: "1px solid #f0eeff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}>
              <span style={{ fontSize: "11px", color: "#7c3aed", fontWeight: 600 }}>
                {selected.length} selected
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); onChange([]); }}
                style={{
                  fontSize: "11px",
                  color: "#b91c1c",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                }}
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AirportSelect({ options = [], value = "", onChange, autoFilled, error, placeholder = "Search airport…" }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Normalize each option to { label, value }
  const normalize = (opt) => {
    const name = opt.airport_name || opt.name || String(opt);
    return {
      label: name,
      value: name,
    };
  };

  const filtered = options
    .map(normalize)
    .filter((o) => o.label.toLowerCase().includes(search.toLowerCase()));

  const selectedLabel = (() => {
    if (!value) return null;
    const found = options.map(normalize).find((o) => o.value === value);
    return found ? found.label : value;
  })();

  const triggerBorderColor = error
    ? "#b91c1c"
    : autoFilled
      ? "#bbf7d0"
      : open
        ? "#6d28d9"
        : "#e0ddf5";

  const triggerBg = autoFilled ? "#f0fdf4" : "#fff";
  const triggerColor = autoFilled
    ? "#166534"
    : selectedLabel
      ? "#1a1a2e"
      : "#999";





  return (
    <div ref={wrapRef} style={{ position: "relative", width: "100%" }}>
      <div
        onClick={() => { setOpen((o) => !o); setSearch(""); }}
        style={{
          padding: "8px 32px 8px 11px",
          border: `1.5px solid ${triggerBorderColor}`,
          borderRadius: open ? "7px 7px 0 0" : "7px",
          fontSize: "13px",
          fontFamily: "system-ui, -apple-system, sans-serif",
          backgroundColor: triggerBg,
          color: triggerColor,
          cursor: "pointer",
          userSelect: "none",
          width: "100%",
          boxSizing: "border-box",
          lineHeight: "1.4",
          position: "relative",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontWeight: autoFilled ? 500 : 400,
          boxShadow: open ? "0 0 0 3px rgba(109,40,217,0.10)" : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
        }}
      >
        {selectedLabel || placeholder}
        <span style={{
          position: "absolute",
          right: "10px",
          top: "50%",
          transform: `translateY(-50%) rotate(${open ? "180deg" : "0deg"})`,
          transition: "transform 0.2s",
          lineHeight: 1,
          pointerEvents: "none",
        }}>
          <svg width="10" height="6" viewBox="0 0 10 6">
            <path d="M1 1l4 4 4-4" stroke="#7c3aed" strokeWidth="1.5" fill="none" strokeLinecap="round" />
          </svg>
        </span>
      </div>

      {open && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          backgroundColor: "#fff",
          border: "1.5px solid #6d28d9",
          borderTop: "none",
          borderRadius: "0 0 9px 9px",
          zIndex: 9999,
          boxShadow: "0 8px 24px rgba(109,40,217,0.14)",
          maxHeight: "220px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f0eeff" }}>
            <input
              autoFocus
              placeholder="Search airports…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                padding: "6px 10px",
                border: "1.5px solid #e0ddf5",
                borderRadius: "6px",
                fontSize: "12px",
                fontFamily: "system-ui, -apple-system, sans-serif",
                outline: "none",
                boxSizing: "border-box",
                color: "#1a1a2e",
              }}
            />
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{
                padding: "12px 14px",
                fontSize: "12px",
                color: "#aaa",
                fontFamily: "system-ui, -apple-system, sans-serif",
                textAlign: "center",
              }}>
                No airports found
              </div>
            ) : (
              filtered.map((opt) => {
                const isSelected = opt.value === value;
                return (
                  <div
                    key={opt.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      onChange(opt.value);
                      setOpen(false);
                      setSearch("");
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "8px 14px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#f5f3ff" : "transparent",
                      transition: "background-color 0.1s",
                      userSelect: "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) e.currentTarget.style.backgroundColor = "#faf5ff";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isSelected ? "#f5f3ff" : "transparent";
                    }}
                  >
                    {/* Radio dot */}
                    <div style={{
                      width: "15px",
                      height: "15px",
                      borderRadius: "50%",
                      border: isSelected ? "2px solid #6d28d9" : "2px solid #ccc",
                      backgroundColor: isSelected ? "#6d28d9" : "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      transition: "all 0.15s",
                    }}>
                      {isSelected && (
                        <div style={{
                          width: "5px",
                          height: "5px",
                          borderRadius: "50%",
                          backgroundColor: "#fff",
                        }} />
                      )}
                    </div>
                    <span style={{
                      fontSize: "13px",
                      fontFamily: "system-ui, -apple-system, sans-serif",
                      color: isSelected ? "#4c1d95" : "#333",
                      fontWeight: isSelected ? 600 : 400,
                    }}>
                      {opt.label}
                    </span>
                  </div>
                );
              })
            )}
          </div>

          {/* Clear selection */}
          {value && (
            <div style={{
              padding: "6px 12px",
              borderTop: "1px solid #f0eeff",
              display: "flex",
              justifyContent: "flex-end",
            }}>
              <button
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                style={{
                  fontSize: "11px",
                  color: "#b91c1c",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                  padding: "2px 6px",
                  borderRadius: "4px",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                }}
              >
                Clear
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}




// ═══════════════════════════════════════════════════════════════════════════════
//  RateRequestModal Component
// ═══════════════════════════════════════════════════════════════════════════════
function RateRequestModal({ isOpen, onClose, leadId }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [autoFilled, setAutoFilled] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  // Dynamic dropdowns from API
  const [petTypes, setPetTypes] = useState([]);
  const [petBreeds, setPetBreeds] = useState([]);
  const [airlineOptions, setAirlineOptions] = useState([]);

  const [airportOptions, setAirportOptions] = useState([]);
  // Fetch pet types + airline names once when modal opens
  useEffect(() => {
    if (!isOpen) return;

    getPetTypes()
      .then((data) => setPetTypes(data))
      .catch((err) => console.error("Error fetching pet types:", err));

    getAirlinesName()
      .then((data) => setAirlineOptions(data || []))
      .catch((err) => console.error("Error fetching airline names:", err));

    getAirports()
      .then((data) => setAirportOptions(data || []))
      .catch((err) => console.error("Error fetching airports:", err));
  }, [isOpen]);

  // Fetch lead data when modal opens
  useEffect(() => {
    if (!isOpen || !leadId) return;
    setLoading(true);
    setForm(BLANK_FORM);
    setAutoFilled([]);
    setErrors({});

    fetchLeadById(leadId)
      .then((data) => {
        if (data) {
          setForm((prev) => ({ ...prev, ...data }));
          setAutoFilled(Object.keys(data));

          if (data.petType) {
            getPetBreeds(data.petType)
              .then((breeds) => setPetBreeds(breeds))
              .catch((err) => console.error("Error fetching breeds:", err));
          }
        }
      })
      .catch((err) => {
        console.error("Error fetching lead:", err);
        showToast("Could not load lead data.", "error");
      })
      .finally(() => setLoading(false));
  }, [isOpen, leadId]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const setField = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: val }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  const isAuto = (k) => autoFilled.includes(k);

  const validate = () => {
    const e = {};
    if (!form.airlinesName || form.airlinesName.length === 0)
      e.airlinesName = "Select at least one airline";
    if (!form.originAirport.trim()) e.originAirport = "Required";
    if (!form.destinationAirport.trim()) e.destinationAirport = "Required";
    if (!form.grossWeight) e.grossWeight = "Required";
    if (!form.petType) e.petType = "Required";
    if (!form.petBreed) e.petBreed = "Required";
    if (!form.dob) e.dob = "Required";
    if (!form.petDimensions.trim()) e.petDimensions = "Required (L×B×H in inches)";
    if (!form.volumetricWeight) e.volumetricWeight = "Required";
    if (!form.freightCharges && !form.overseasCharges)
      e.charges = "Select at least one charge type";
    if (form.overseasCharges && form.overseasRequestedFor.length === 0)
      e.overseasRequestedFor = "Select at least one overseas option";
    
    if (
      (form.petCrate === "CR82" || form.petCrate === "Soft Bag") &&
      !form.customCrateSize.trim()
    ) {
      e.customCrateSize = "Please enter crate size based on pet dimension";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const result = await submitRateRequest(form, leadId);
      if (result.status === "True") {
        showToast("Request submitted! Mail sent to authorised person.");
        setTimeout(() => {
          onClose();
          setForm(BLANK_FORM);
          setAutoFilled([]);
        }, 1600);
      } else {
        showToast(result.message || "Submission failed.", "error");
      }
    } catch (err) {
      console.error("Submission error:", err);
      showToast("Submission failed. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) onClose();
  };

  const handlePetCrateChange = (e) => {
    const val = e.target.value;
    const numericCrates = ["17", "35", "48", "66", "89", "147"];
    const isNumeric = numericCrates.includes(val);

    setForm((p) => ({
      ...p,
      petCrate: val,
      volumetricWeight: isNumeric ? val : "",
      customCrateSize: "",   // ── NEW: reset whenever crate changes
    }));
    setErrors((p) => ({ ...p, petCrate: "", volumetricWeight: "", customCrateSize: "" }));
  };


  const handlePetTypeChange = (e) => {
    const selectedType = e.target.value;
    setForm((p) => ({ ...p, petType: selectedType, petBreed: "" }));
    setErrors((p) => ({ ...p, petType: "", petBreed: "" }));

    if (selectedType) {
      getPetBreeds(selectedType)
        .then((breeds) => setPetBreeds(breeds))
        .catch((err) => console.error("Error fetching breeds:", err));
    } else {
      setPetBreeds([]);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={css.overlay} onClick={handleClose}>
      <div style={css.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={css.header}>
          <div style={css.headerLeft}>
            <div style={css.headerIcon}>✈</div>
            <div>
              <p style={css.headerTitle}>Rate Request</p>
              <p style={css.headerSub}>
                Lead ID:&nbsp;
                <strong style={{ color: "#fff" }}>{leadId}</strong>
              </p>
            </div>
          </div>
          <button
            style={css.closeBtn}
            onClick={handleClose}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.28)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.15)"; }}
          >
            ✕
          </button>
        </div>

        {/* Loading */}
        {loading ? (
          <div style={css.loadingWrap}>
            <Spinner size={32} color="#7c3aed" />
            <p style={css.loadingText}>Fetching lead details…</p>
            <div style={css.skelGrid}>
              {[...Array(6)].map((_, i) => (
                <div key={i} style={css.skel} />
              ))}
            </div>
          </div>
        ) : (
          /* Form */
          <div style={css.body}>
            <Section title="Flight Details" />

            {/* Airlines Name — full width multi-select dropdown */}
            <div style={css.grid1}>
              <Field label="Airlines Name" error={errors.airlinesName}>
                <AirlineMultiSelect
                  options={airlineOptions}
                  selected={form.airlinesName}
                  onChange={(val) => {
                    setForm((p) => ({ ...p, airlinesName: val }));
                    setErrors((p) => ({ ...p, airlinesName: "" }));
                  }}
                  autoFilled={isAuto("airlinesName")}
                  error={errors.airlinesName}
                />
              </Field>
            </div>

            <div style={css.grid3}>

              <Field label="Origin Airport" error={errors.originAirport}>
                <AirportSelect
                  options={airportOptions}
                  value={form.originAirport}
                  onChange={(val) => {
                    setForm((p) => ({ ...p, originAirport: val }));
                    setErrors((p) => ({ ...p, originAirport: "" }));
                  }}
                  autoFilled={isAuto("originAirport")}
                  error={errors.originAirport}
                  placeholder="Select origin airport"
                />
              </Field>

              <Field label="Destination Airport" error={errors.destinationAirport}>
                <AirportSelect
                  options={airportOptions}
                  value={form.destinationAirport}
                  onChange={(val) => {
                    setForm((p) => ({ ...p, destinationAirport: val }));
                    setErrors((p) => ({ ...p, destinationAirport: "" }));
                  }}
                  autoFilled={isAuto("destinationAirport")}
                  error={errors.destinationAirport}
                  placeholder="Select destination airport"
                />
              </Field>

              <Field label="Gross Weight (incl. Pet)" error={errors.grossWeight}>
                <div style={css.inputWithUnit}>
                  <HoverInput
                    autoFilled={isAuto("grossWeight")}
                    type="number"
                    placeholder="0.00"
                    value={form.grossWeight}
                    onChange={setField("grossWeight")}
                    style={{ paddingRight: "36px" }}
                  />
                  <span style={css.unitLabel}>kg</span>
                </div>
              </Field>
            </div>

            <Section title="Pet Details" />

            <div style={css.grid3}>
              <Field label="Pet Type" error={errors.petType}>
                <HoverSelect
                  autoFilled={isAuto("petType")}
                  value={form.petType}
                  onChange={handlePetTypeChange}
                >
                  <option value="">Select</option>
                  {petTypes.map((pt) => (
                    <option key={pt.id} value={pt.id}>
                      {pt.petType}
                    </option>
                  ))}
                </HoverSelect>
              </Field>
              <Field label="Pet Breed" error={errors.petBreed}>
                <HoverSelect
                  autoFilled={isAuto("petBreed")}
                  value={form.petBreed}
                  onChange={setField("petBreed")}
                >
                  <option value="">Select</option>
                  {petBreeds.map((pb) => (
                    <option key={pb.id} value={pb.id}>
                      {pb.PetBreedType}
                    </option>
                  ))}
                </HoverSelect>
              </Field>
              <Field label="Date of Birth (D.O.B)" error={errors.dob}>
                <HoverInput
                  autoFilled={isAuto("dob")}
                  type="date"
                  value={form.dob}
                  onChange={setField("dob")}
                />
              </Field>
            </div>

            <div style={css.grid3}>
              <Field label="Pet Dimensions (L×B×H)" error={errors.petDimensions}>
                <HoverInput
                  autoFilled={isAuto("petDimensions")}
                  placeholder="e.g. 60×40×45  inches"
                  value={form.petDimensions}
                  onChange={setField("petDimensions")}
                  style={{ fontFamily: "monospace" }}
                />
              </Field>

              <Field label="Pet Crate" error={errors.petCrate}>
                <HoverSelect
                  autoFilled={isAuto("petCrate")}
                  value={form.petCrate}
                  onChange={handlePetCrateChange}
                >
                  <option value="0">Select an option</option>
                  <option value="17">(24" X 16" X 16")</option>
                  <option value="35">(28" X 21" X 22")</option>
                  <option value="48">(32" X 23" X 24")</option>
                  <option value="66">(36" X 25" X 27")</option>
                  <option value="89">(40" X 27" X 30")</option>
                  <option value="147">(48" X 32" X 35")</option>
                  <option value="CR82">CR82</option>
                  <option value="Soft Bag">Soft Bag</option>
                </HoverSelect>
              </Field>

              <Field label="Volumetric Weight" error={errors.volumetricWeight}>
                <div style={css.inputWithUnit}>
                  <HoverInput
                    autoFilled={false}
                    type="number"
                    placeholder="0.00"
                    value={form.volumetricWeight}
                    onChange={setField("volumetricWeight")}
                    style={{ paddingRight: "36px" }}
                  />
                  <span style={css.unitLabel}>kg</span>
                </div>
              </Field>
            </div>

            {(form.petCrate === "CR82" || form.petCrate === "Soft Bag") && (
              <div style={{ marginTop: "-4px", marginBottom: "12px" }}>
                <Field label="Crate Size (based on Pet Dimension)" error={errors.customCrateSize}>
                  <HoverInput
                    autoFilled={false}
                    placeholder='e.g. 28" X 21" X 22"'
                    value={form.customCrateSize}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, customCrateSize: e.target.value }));
                      setErrors((p) => ({ ...p, customCrateSize: "" }));
                    }}
                    style={{ fontFamily: "monospace" }}
                  />
                  <p style={{
                    margin: "4px 0 0",
                    fontSize: "11px",
                    color: "#b45309",
                    fontFamily: "system-ui, -apple-system, sans-serif",
                  }}>
                    ⚠️ Please enter crate size manually based on your pet's dimensions.
                  </p>
                </Field>
              </div>
            )}

            <Section title="Charge Types" />
            {errors.charges && (
              <p style={{ ...css.errMsg, marginBottom: "10px" }}>⚠ {errors.charges}</p>
            )}

            <div style={css.chargesRow}>
              {[
                { key: "freightCharges", label: "Freight Charges", icon: "📦" },
                { key: "overseasCharges", label: "Overseas Charges", icon: "🌐" },
              ].map(({ key, label, icon }) => (
                <div
                  key={key}
                  style={{ ...css.checkCard, ...(form[key] ? css.checkCardOn : {}) }}
                  onClick={() => {
                    setForm((p) => ({
                      ...p,
                      [key]: !p[key],
                      // Reset sub-options when unchecking overseas
                      ...(key === "overseasCharges" && p[key]
                        ? { overseasRequestedFor: [] }
                        : {}),
                    }));
                    setErrors((p) => ({ ...p, charges: "", overseasRequestedFor: "" }));
                  }}
                  onMouseEnter={(e) => {
                    if (!form[key]) e.currentTarget.style.borderColor = "#a78bfa";
                  }}
                  onMouseLeave={(e) => {
                    if (!form[key]) e.currentTarget.style.borderColor = "#e0ddf5";
                  }}
                >
                  <div style={{ ...css.checkbox, ...(form[key] ? css.checkboxOn : {}) }}>
                    {form[key] && (
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <path d="M1 4L3 6.5L7 1.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    fontFamily: "system-ui, -apple-system, sans-serif",
                    color: form[key] ? "#4c1d95" : "#444",
                  }}>
                    {icon} {label}
                  </span>
                </div>
              ))}
            </div>

            {/* Overseas sub-options — shown only when overseasCharges is checked */}
            {form.overseasCharges && (
              <div style={{ marginTop: "-4px", marginBottom: "16px" }}>
                <Field label="Overseas Requested For" error={errors.overseasRequestedFor}>
                  <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    padding: "10px 12px",
                    border: `1.5px solid ${errors.overseasRequestedFor ? "#b91c1c" : "#e0ddf5"}`,
                    borderRadius: "9px",
                    backgroundColor: "#faf8ff",
                  }}>
                    {OVERSEAS_OPTIONS.map((opt) => {
                      const checked = form.overseasRequestedFor.includes(opt);
                      return (
                        <div
                          key={opt}
                          onClick={() => {
                            const next = checked
                              ? form.overseasRequestedFor.filter((v) => v !== opt)
                              : [...form.overseasRequestedFor, opt];
                            setForm((p) => ({ ...p, overseasRequestedFor: next }));
                            setErrors((p) => ({ ...p, overseasRequestedFor: "" }));
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "7px",
                            padding: "7px 12px",
                            border: `1.5px solid ${checked ? "#6d28d9" : "#e0ddf5"}`,
                            borderRadius: "7px",
                            cursor: "pointer",
                            backgroundColor: checked ? "#f5f3ff" : "#fff",
                            userSelect: "none",
                            transition: "all 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            if (!checked) e.currentTarget.style.borderColor = "#a78bfa";
                          }}
                          onMouseLeave={(e) => {
                            if (!checked) e.currentTarget.style.borderColor = "#e0ddf5";
                          }}
                        >
                          <div style={{
                            width: "14px",
                            height: "14px",
                            borderRadius: "4px",
                            border: checked ? "2px solid #6d28d9" : "2px solid #ccc",
                            backgroundColor: checked ? "#6d28d9" : "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            transition: "all 0.15s",
                          }}>
                            {checked && (
                              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                <path d="M1 4L3 6.5L7 1.5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            )}
                          </div>
                          <span style={{
                            fontSize: "12px",
                            fontWeight: checked ? 600 : 400,
                            fontFamily: "system-ui, -apple-system, sans-serif",
                            color: checked ? "#4c1d95" : "#444",
                            whiteSpace: "nowrap",
                          }}>
                            {opt}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </Field>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div style={css.footer}>
            <button
              style={css.cancelBtn}
              onClick={handleClose}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#6d28d9";
                e.currentTarget.style.color = "#6d28d9";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e0ddf5";
                e.currentTarget.style.color = "#666";
              }}
            >
              Cancel
            </button>
            <button
              style={{
                ...css.submitBtn,
                opacity: submitting ? 0.65 : 1,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
              onClick={handleSubmit}
              disabled={submitting}
              onMouseEnter={(e) => {
                if (!submitting) e.currentTarget.style.transform = "translateY(-1px)";
              }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
            >
              {submitting ? (
                <><Spinner size={13} color="#fff" /> Submitting…</>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                  Submit Request
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div
          style={{
            ...css.toast,
            backgroundColor: toast.type === "error" ? "#7f1d1d" : "#14532d",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {toast.type === "error" ? "✕" : "✓"} {toast.msg}
        </div>
      )}
    </div>
  );
}

export default RateRequestModal;
